import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ResearchLayout from '../../components/ResearchLayout';
import { protocolService } from '../../services/protocolService';

const ProgramAdminViewProtocol = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [protocol, setProtocol] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadProtocolDetails();
  }, [id]);

  const loadProtocolDetails = async () => {
    try {
      setLoading(true);
      const response = await protocolService.getProtocol(id, true);
      
      if (!response.protocol) {
        throw new Error('Protocol not found');
      }
      
      setProtocol(response.protocol);
      setQuestions(response.protocol.questions || []);
    } catch (error) {
      console.error('Error loading protocol:', error);
      setError(error.message || 'Failed to load protocol');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <ResearchLayout pageTitle="View Protocol">
        <div className="container-fluid">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading protocol details...</p>
          </div>
        </div>
      </ResearchLayout>
    );
  }

  if (error) {
    return (
      <ResearchLayout pageTitle="View Protocol">
        <div className="container-fluid">
          <div className="alert alert-danger" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/program-admin/protocols')}
          >
            <i className="fas fa-arrow-left me-2"></i>
            Back to Protocols
          </button>
        </div>
      </ResearchLayout>
    );
  }

  return (
    <ResearchLayout pageTitle={protocol?.name || 'View Protocol'}>
      <div className="container-fluid">
        {/* Breadcrumb */}
        <div className="row mb-4">
          <div className="col-12">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/program-admin/dashboard">
                    <i className="fas fa-home me-1"></i>
                    Dashboard
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/program-admin/protocols">
                    <i className="fas fa-clipboard-list me-1"></i>
                    Protocols
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  View Protocol
                </li>
              </ol>
            </nav>
          </div>
        </div>
        
        {/* Protocol Details Card */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-primary text-white">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="fas fa-clipboard-list me-2"></i>
                    Protocol Details
                  </h5>
                  <div>
                    <Link 
                      to={`/program-admin/edit-protocol/${protocol.id}`}
                      className="btn btn-sm btn-light me-2"
                    >
                      <i className="fas fa-edit me-1"></i>
                      Edit
                    </Link>
                    <Link 
                      to={`/program-admin/link-questions?protocol=${protocol.id}`}
                      className="btn btn-sm btn-info"
                    >
                      <i className="fas fa-link me-1"></i>
                      Manage Questions
                    </Link>
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-8">
                    <h4>{protocol.name}</h4>
                    <p className="text-muted">{protocol.description}</p>
                    
                    <div className="mt-4">
                      <h6 className="text-uppercase text-muted mb-3">Status</h6>
                      <div className="d-flex align-items-center gap-2">
                        {protocol.is_active ? (
                          <span className="badge bg-success p-2">
                            <i className="fas fa-check-circle me-1"></i> Active
                          </span>
                        ) : (
                          <span className="badge bg-secondary p-2">
                            <i className="fas fa-times-circle me-1"></i> Inactive
                          </span>
                        )}
                        <span className={`badge ${protocol.show_ai_response ? 'bg-info' : 'bg-secondary'} p-2`}
                          title={protocol.show_ai_response ? 'Doctors can view AI assessment' : 'Doctors cannot view AI assessment'}>
                          <i className="fas fa-robot me-1"></i>
                          {protocol.show_ai_response ? 'AI Viewed' : 'AI Hidden'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-md-4">
                    <div className="card bg-light border-0">
                      <div className="card-body">
                        <h6 className="text-uppercase text-muted mb-3">Information</h6>
                        <ul className="list-unstyled">
                          <li className="mb-2">
                            <i className="fas fa-user text-primary me-2"></i>
                            Created by: {protocol.created_by_name || 'Unknown'}
                          </li>
                          <li className="mb-2">
                            <i className="fas fa-calendar-alt text-primary me-2"></i>
                            Created: {new Date(protocol.created_at).toLocaleDateString()}
                          </li>
                          {protocol.updated_at && (
                            <li className="mb-2">
                              <i className="fas fa-edit text-primary me-2"></i>
                              Last Updated: {new Date(protocol.updated_at).toLocaleDateString()}
                            </li>
                          )}
                          <li className="mb-2">
                            <i className="fas fa-question-circle text-primary me-2"></i>
                            Questions: {questions.length}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Questions Section */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white">
                <h5 className="mb-0">
                  <i className="fas fa-question-circle text-info me-2"></i>
                  Protocol Questions
                </h5>
              </div>
              <div className="card-body">
                {questions.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th width="5%">#</th>
                          <th width="70%">Question Text</th>
                          <th width="15%">Type</th>
                          <th width="10%">Required</th>
                        </tr>
                      </thead>
                      <tbody>
                        {questions.map((question, index) => (
                          <tr key={question.id}>
                            <td>{index + 1}</td>
                            <td>{question.text}</td>
                            <td>
                              <span className="badge bg-light text-dark">
                                {question.type || 'Text'}
                              </span>
                            </td>
                            <td>
                              {question.required ? (
                                <span className="badge bg-success">Yes</span>
                              ) : (
                                <span className="badge bg-light text-dark">No</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-question-circle fa-3x text-muted mb-3"></i>
                    <h5>No Questions Linked</h5>
                    <p className="text-muted">This protocol has no questions linked to it yet.</p>
                    <Link
                      to={`/program-admin/link-questions?protocol=${protocol.id}`}
                      className="btn btn-primary mt-2"
                    >
                      <i className="fas fa-link me-2"></i>
                      Link Questions
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ResearchLayout>
  );
};

export default ProgramAdminViewProtocol;